import React, { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AsideSection } from "@/components/AsideSection";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Receipt, Paperclip, Upload, Download, Trash2, FileText } from "lucide-react";

const ACCOUNT_TYPES = ["Labour", "Materials", "Subcontractor", "Plant Hire", "Delivery", "Other"];
const COST_TYPES = ["Estimated", "Actual", "One-off", "Recurring"];
const VAT_OPTS = [["Standard", "Standard (20%)"], ["Reduced", "Reduced (5%)"], ["Zero", "Zero (0%)"], ["Exempt", "Exempt"]];
const VAT_RATE = { Standard: 0.2, Reduced: 0.05, Zero: 0, Exempt: 0 };
const gbp = (v) => "£" + (Number(v) || 0).toFixed(2);

function Lbl({ children }) {
  return <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</span>;
}

export default function CostForm({ taskId, quoteNumber, cost, depots = [], onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState(cost ? {
    account_type: cost.account_type || "", rate: cost.rate || "", title: cost.title || "",
    reference: cost.reference || "", cost_date: cost.cost_date || today, depot: cost.depot || "HQ",
    cost_type: cost.cost_type || "", quantity: cost.quantity ?? 0, amount: cost.amount ?? 0, vat: cost.vat || "Standard",
  } : {
    account_type: "", rate: "", title: "", reference: "", cost_date: today,
    depot: "HQ", cost_type: "", quantity: 0, amount: 0, vat: "Standard",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState(cost?.attachments || []);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const totals = useMemo(() => {
    const q = Number(f.quantity) || 0;
    const amt = Number(f.amount) || 0;
    const sub = (q > 0 ? q : 1) * amt;
    const vat = sub * (VAT_RATE[f.vat] ?? 0);
    return { sub, vat, total: sub + vat };
  }, [f.quantity, f.amount, f.vat]);
  const depotOpts = Array.from(new Set(["HQ", ...depots.filter(Boolean)]));
  const isEdit = !!cost?.id;

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const payload = { ...f, quantity: Number(f.quantity) || 0, amount: Number(f.amount) || 0 };
    try {
      let r;
      if (isEdit) r = await api.put(`/costs/${cost.id}`, { ...payload, quote_number: cost.quote_number || quoteNumber || null });
      else r = await api.post(`/tasks/${taskId}/costs`, { ...payload, quote_number: quoteNumber || null });
      toast.success(isEdit ? "Cost updated" : "Cost added");
      onSaved(r.data.cost);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save cost"); }
    finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isEdit) { toast.error("Save the cost first, then attach files"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/costs/${cost.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((a) => [...a, r.data.attachment]);
      toast.success("File attached");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (att) => {
    try {
      const r = await api.get(`/files/${att.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download file"); }
  };

  const removeAtt = async (att) => {
    try { await api.delete(`/costs/${cost.id}/attachments/${att.id}`); setAttachments((a) => a.filter((x) => x.id !== att.id)); }
    catch { toast.error("Could not remove attachment"); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="cost-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit Cost" : "Cost Form"}{quoteNumber ? ` · Quote ${quoteNumber}` : ""}</DialogTitle></DialogHeader>
        <AsideSection first icon={Receipt} title="Cost information" description="Basic information about the cost.">
          <div><Lbl>Account Type</Lbl>
            <Select value={f.account_type} onValueChange={(v) => set("account_type", v)}>
              <SelectTrigger className="mt-1" data-testid="cost-account-type"><SelectValue placeholder="Please select" /></SelectTrigger>
              <SelectContent>{ACCOUNT_TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Lbl>Rate</Lbl>
            <Input value={f.rate} onChange={(e) => set("rate", e.target.value)} placeholder="e.g. per day" className="mt-1" /></div>
          <div><Lbl>Title</Lbl>
            <Input value={f.title} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="cost-title" /></div>
          <div><Lbl>Reference</Lbl>
            <Input value={f.reference} onChange={(e) => set("reference", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Lbl>Cost Date</Lbl>
              <Input type="date" value={f.cost_date} onChange={(e) => set("cost_date", e.target.value)} className="mt-1" /></div>
            <div><Lbl>Depot</Lbl>
              <Select value={f.depot} onValueChange={(v) => set("depot", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{depotOpts.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Lbl>Cost Type</Lbl>
              <Select value={f.cost_type} onValueChange={(v) => set("cost_type", v)}>
                <SelectTrigger className="mt-1" data-testid="cost-type"><SelectValue placeholder="Please select" /></SelectTrigger>
                <SelectContent>{COST_TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Lbl>Quantity</Lbl>
              <Input type="number" value={f.quantity} onChange={(e) => set("quantity", e.target.value)} className="mt-1" data-testid="cost-qty" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Lbl>Amount</Lbl>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">£</span>
                <Input type="number" className="pl-6" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="cost-amount" />
              </div>
            </div>
            <div><Lbl>VAT</Lbl>
              <Select value={f.vat} onValueChange={(v) => set("vat", v)}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{VAT_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </AsideSection>

        <AsideSection icon={Paperclip} title="Files" description="Attach receipts or purchase orders (PDF or image, up to 10MB).">
          {!isEdit ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-400" data-testid="cost-files-hint">
              Save the cost first, then re-open it to attach files.
            </div>
          ) : (
            <>
              <label className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center block cursor-pointer hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors" data-testid="cost-file-dropzone">
                {uploading ? <span className="inline-flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</span>
                  : <span className="inline-flex items-center gap-2 text-sm text-slate-500"><Upload className="w-4 h-4" /> Drop files here to upload</span>}
                <input type="file" className="hidden" onChange={onFile} data-testid="cost-file-input" />
              </label>
              {attachments.length > 0 && (
                <div className="space-y-1.5">
                  {attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid={`cost-attachment-${a.id}`}>
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="flex-1 truncate text-slate-700">{a.filename}</span>
                      <button onClick={() => download(a)} className="text-slate-400 hover:text-indigo-600 p-1" title="Download"><Download className="w-4 h-4" /></button>
                      <button onClick={() => removeAtt(a)} className="text-slate-400 hover:text-rose-600 p-1" title="Remove"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </AsideSection>

        <div className="flex items-center justify-end gap-8 border-t border-slate-100 pt-4 text-right">
          <div><div className="text-[11px] text-slate-400">Sub Total</div><div className="font-bold text-slate-800" data-testid="cost-subtotal">{gbp(totals.sub)}</div></div>
          <div><div className="text-[11px] text-slate-400">VAT Total</div><div className="font-bold text-slate-800">{gbp(totals.vat)}</div></div>
          <div><div className="text-[11px] text-slate-400">Total</div><div className="font-bold text-indigo-700" data-testid="cost-total">{gbp(totals.total)}</div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="cost-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
